import { debounce } from '../../utils/debounce'


/**
 * Search Input - extends GMXInput functionality
 */

const CONSTANTS = {
	MIN_AUTOCOMPLETE_CHARS: 1,
	MIN_SEARCH_CHARS: 2,
} as const

export class GMXSearch extends HTMLElement {
	inputEl: HTMLInputElement
	searchIcon: HTMLElement | null
	cancelBtn: HTMLButtonElement | null
	searchBtn: HTMLButtonElement | null
	dropdownEl: HTMLElement | null
	helperTextEl: HTMLElement | null

	private autocompleteResults: string[] = []
	private selectedIndex = -1
	/**
	 * Tracks whether the user is navigating via keyboard (true) or mouse (false).
	 * Used to show focus outline only during keyboard navigation for better UX.
	 */
	private isKeyboardNavigation = false
	/**
	 * Stores the most recent query used for autocomplete so we can ignore
	 * responses for older, out-of-date queries.
	 */
	private lastAutocompleteQuery = ''
	/**
	 * Set once a search is committed, so a debounce timer scheduled before it cannot still fire
	 * and reopen the dropdown over the results. Cleared as soon as the user types again.
	 */
	private isAutocompleteSuppressed = false

	private boundInputEventHandler = this._handleInputEvent.bind(this)
	private boundCancelHandler = this._handleCancel.bind(this)
	private boundSearchHandler = this._handleSearch.bind(this)
	private boundKeydownHandler = this._handleKeydown.bind(this)
	private boundFocusHandler = this._handleFocus.bind(this)
	private boundBlurHandler = this._handleBlur.bind(this)
	private boundDocumentClickHandler = this._handleDocumentClick.bind(this)
	private boundWindowResizeHandler = this._handleWindowResize.bind(this)

	static observedAttributes = ['data-theme']


	connectedCallback(): void {
		this.inputEl = this.querySelector('input') as HTMLInputElement
		this.searchIcon = this.querySelector('.search-icon')
		this.cancelBtn = this.querySelector('.cancel-btn')
		this.searchBtn = this.querySelector('.search-btn')
		this.dropdownEl = this.querySelector('.autocomplete-dropdown')
		this.helperTextEl = this.querySelector('.helper-text')

		if (!this.inputEl) {
			window.console.error(`ERROR: GMX Search missing <input> element`)
			return
		}

		// Create dropdown if it doesn't exist
		if (!this.dropdownEl) {
			this.dropdownEl = document.createElement('div')
			this.dropdownEl.className = 'autocomplete-dropdown'
			this.dropdownEl.setAttribute('data-theme', this.getAttribute('data-theme') || 'light')
			this.dropdownEl.setAttribute('role', 'listbox')
			this.dropdownEl.setAttribute('aria-label', 'autocomplete results')
			this.appendChild(this.dropdownEl)
		}

		// Set up ARIA combobox attributes
		const dropdownId = this.dropdownEl.id || `autocomplete-${Math.random().toString(36).substr(2, 9)}`
		this.dropdownEl.id = dropdownId

		this.inputEl.setAttribute('role', 'combobox')
		this.inputEl.setAttribute('aria-autocomplete', 'list')
		this.inputEl.setAttribute('aria-expanded', 'false')
		// Note: aria-controls is set dynamically when dropdown is shown to avoid
		// referencing an ID that may not be visible/valid in the accessibility tree
		this.inputEl.setAttribute('aria-haspopup', 'listbox')

		// Input events
		this.inputEl.addEventListener('input', this.boundInputEventHandler)
		this.inputEl.addEventListener('keydown', this.boundKeydownHandler)
		this.inputEl.addEventListener('focus', this.boundFocusHandler)
		this.inputEl.addEventListener('blur', this.boundBlurHandler)

		// Cancel button
		if (this.cancelBtn) {
			this.cancelBtn.addEventListener('click', this.boundCancelHandler)
		}

		// Search button
		if (this.searchBtn) {
			this.searchBtn.addEventListener('click', this.boundSearchHandler)
		}

		// Document click to close dropdown
		document.addEventListener('click', this.boundDocumentClickHandler)

		// Position dropdown on window resize
		window.addEventListener('resize', this.boundWindowResizeHandler)

		// Initial state – sync UI in case input has a pre-existing value
		const hasValue = this.inputEl.value.trim() !== ''
		this.inputEl.classList.toggle('filled', hasValue)
		if (this.searchIcon) {
			this.searchIcon.classList.toggle('hidden', hasValue)
			this.searchIcon.style.opacity = hasValue ? '0' : ''
			this.searchIcon.style.visibility = hasValue ? 'hidden' : ''
		}
		this._updateButtons()
	}

	disconnectedCallback(): void {
		if (this.inputEl) {
			this.inputEl.removeEventListener('input', this.boundInputEventHandler)
			this.inputEl.removeEventListener('keydown', this.boundKeydownHandler)
			this.inputEl.removeEventListener('focus', this.boundFocusHandler)
			this.inputEl.removeEventListener('blur', this.boundBlurHandler)
		}
		if (this.cancelBtn) {
			this.cancelBtn.removeEventListener('click', this.boundCancelHandler)
		}
		if (this.searchBtn) {
			this.searchBtn.removeEventListener('click', this.boundSearchHandler)
		}
		document.removeEventListener('click', this.boundDocumentClickHandler)
		window.removeEventListener('resize', this.boundWindowResizeHandler)
	}

	/**
	 * Search for a caller-supplied term, bypassing the typing flow.
	 *
	 * Lets callers that already know the term (e.g. a common-topic button) run a search without
	 * synthesizing `input`/`keydown` events. Synthesizing `input` starts a debounced autocomplete
	 * fetch, and Enter is routed to the selected suggestion rather than the term whenever that
	 * fetch has populated the dropdown — so the search that ran depended on which won the race.
	 * Going straight to the search removes that ambiguity; `_handleSearch()` retires whatever
	 * autocomplete is pending or open.
	 *
	 * @param query - The term to search for.
	 */
	search(query: string): void {
		if (!this.inputEl) return

		const trimmedQuery = query.trim()
		if (!trimmedQuery) return

		this.inputEl.value = trimmedQuery
		this.inputEl.classList.add('filled')

		if (this.searchIcon) {
			this.searchIcon.classList.add('hidden')
			this.searchIcon.style.opacity = '0'
			this.searchIcon.style.visibility = 'hidden'
		}

		this._updateButtons()
		this._handleSearch()
	}

	private _handleInputEvent(): void {
		if (!this.inputEl) return
		const value = this.inputEl.value.trim()
		const hasValue = value !== ''

		this.inputEl.classList.toggle('filled', hasValue)
		this._updateButtons()

		// Show autocomplete dropdown when user has typed MIN_AUTOCOMPLETE_CHARS+ characters
		if (value.length >= CONSTANTS.MIN_AUTOCOMPLETE_CHARS) {
			this._showAutocomplete(value)
		} else {
			this._hideAutocomplete()
		}
	}

	private _handleCancel(): void {
		if (!this.inputEl) return
		this.inputEl.value = ''
		this.inputEl.classList.remove('filled')
		this._updateButtons()
		this._hideAutocomplete()
		this.lastAutocompleteQuery = ''
		this.inputEl.focus()
	}

	private _handleSearch(): void {
		if (!this.inputEl) return
		const query = this.inputEl.value.trim()
		if (query.length < CONSTANTS.MIN_SEARCH_CHARS) return // enforce MIN_SEARCH_CHARS+ chars

		// Committing to a search retires autocomplete, whichever entry point got here. All three
		// of these are needed: the flag stops a debounce timer that has not fired yet, the empty
		// query makes an already-issued response read as stale, and the hide closes a dropdown
		// that is open right now. Otherwise autocomplete resurfaces over the results in the
		// same-page pattern, where nothing navigates away.
		this.isAutocompleteSuppressed = true
		this.lastAutocompleteQuery = ''
		this.autocompleteResults = []
		this._hideAutocomplete()

		// Dispatch a cancelable search event so consumers can intercept or prevent navigation
		const searchEvent = new CustomEvent('search', {
			detail: { query },
			bubbles: true,
			composed: true,
			cancelable: true,
		})
		const notCancelled = this.dispatchEvent(searchEvent)

		if (!notCancelled) {
			return
		}

		const resultsPageUrl = this.getAttribute('data-results-page-url')
		if (!resultsPageUrl) return

		const baseUrl = window.location.origin || ''
		const url = new URL(resultsPageUrl, baseUrl)
		url.searchParams.set('q', query)

		window.location.href = url.toString()
	}

	private _handleKeydown(e: KeyboardEvent): void {
		const dropdownVisible = !!this.dropdownEl?.classList.contains('show')

		if (!dropdownVisible && !['Enter', 'Escape'].includes(e.key)) return

		switch (e.key) {
			case 'Enter':
				e.preventDefault()
				if (this.selectedIndex >= 0 && this.autocompleteResults.length > 0) {
					this._selectAutocompleteItem(this.selectedIndex)
				} else {
					this._handleSearch()
				}
				break
			case 'ArrowDown':
				if (dropdownVisible) {
					e.preventDefault()
					this._navigateAutocomplete(1)
				}
				break
			case 'ArrowUp':
				if (dropdownVisible) {
					e.preventDefault()
					this._navigateAutocomplete(-1)
				}
				break
			case 'Tab':
				if (dropdownVisible && this.selectedIndex >= 0 && this.autocompleteResults.length > 0) {
					e.preventDefault()
					// Fill input with selected item
					this.inputEl.value = this.autocompleteResults[this.selectedIndex]
					this.inputEl.classList.add('filled')
					this._updateButtons()
					this._hideAutocomplete()
					// Focus the cancel button
					if (this.cancelBtn) {
						this.cancelBtn.focus()
					}
				} else if (dropdownVisible) {
					this._hideAutocomplete()
				}
				break
			case 'Escape':
				if (dropdownVisible) {
					this._hideAutocomplete()
				}
				break
			case 'Home':
				if (dropdownVisible) {
					e.preventDefault()
					this._navigateAutocomplete('start')
				}
				break
			case 'End':
				if (dropdownVisible) {
					e.preventDefault()
					this._navigateAutocomplete('end')
				}
				break
		}
	}

	private _handleFocus(): void {
		// Hide search icon on focus
		if (this.searchIcon) {
			this.searchIcon.style.opacity = '0'
			this.searchIcon.style.visibility = 'hidden'
			this.searchIcon.classList.add('hidden')
		}
		this._updateButtons()
	}

	private _handleBlur(): void {
		// Only show icon if input is empty
		if (this.inputEl && this.inputEl.value.trim() === '' && this.searchIcon) {
			this.searchIcon.style.opacity = ''
			this.searchIcon.style.visibility = ''
			this.searchIcon.classList.remove('hidden')
		}
		// Use requestAnimationFrame for better performance than setTimeout
		requestAnimationFrame(() => this._updateButtons())
	}

	private _updateButtons(): void {
		if (!this.inputEl) return
		const value = this.inputEl.value.trim()
		const hasMinChars = value.length >= CONSTANTS.MIN_SEARCH_CHARS

		// Cancel button (X) - inside input: show after MIN_SEARCH_CHARS+ characters
		if (this.cancelBtn) {
			this.cancelBtn.style.opacity = hasMinChars ? '1' : '0'
			this.cancelBtn.style.visibility = hasMinChars ? 'visible' : 'hidden'
			this.cancelBtn.style.pointerEvents = hasMinChars ? 'auto' : 'none'
		}

		// Search button (arrow) - outside input: always show
		if (this.searchBtn) {
			this.searchBtn.style.opacity = '1'
			this.searchBtn.style.visibility = 'visible'
			this.searchBtn.style.pointerEvents = 'auto'
		}
	}

	private _handleDocumentClick(e: Event): void {
		if (!this.contains(e.target as Node)) {
			this._hideAutocomplete()
		}
	}

	private _positionDropdown(): void {
		if (!this.dropdownEl) return

		// The CSS handles positioning, we just need to ensure width matches
		this.dropdownEl.style.width = `${this.offsetWidth}px`
	}

	private _showAutocomplete(value: string): void {
		if (!this.dropdownEl) return

		// Fresh input means the visitor is searching again, so autocomplete is welcome once more.
		this.isAutocompleteSuppressed = false

		// Always dispatch autocomplete-request so consumers can provide suggestions
		this.dispatchEvent(
			new CustomEvent('autocomplete-request', {
				detail: { query: value },
				bubbles: true,
				composed: true,
			})
		)

		const autocompletePath = this.getAttribute('data-autocomplete-path')
		if (!autocompletePath) {
			// If no service is configured, rely entirely on external listeners
			return
		}

		this._debouncedFetchAutocomplete(value, autocompletePath)
	}

	private _debouncedFetchAutocomplete = debounce((value: string, autocompletePath: string) => {
		if (this.isAutocompleteSuppressed) return

		const trimmedValue = value.trim()
		if (trimmedValue.length < CONSTANTS.MIN_AUTOCOMPLETE_CHARS) return

		this.lastAutocompleteQuery = trimmedValue
		void this._fetchAutocomplete(trimmedValue, autocompletePath)
	}, 200)

	private async _fetchAutocomplete(query: string, autocompletePath: string): Promise<void> {
		const baseUrl = window.location.origin || ''
		const url = new URL(autocompletePath, baseUrl)

		const maxAttr = this.getAttribute('data-autocomplete-max')
		const max = maxAttr ? parseInt(maxAttr, 10) || 20 : 20

		url.searchParams.set('q', query)
		url.searchParams.set('max', String(max))

		try {
			const response = await fetch(url.toString())
			if (!response.ok) return

			const data = await response.json()

			// Ignore responses for stale queries
			if (query !== this.lastAutocompleteQuery) return

			const suggestions = this._extractAutocompleteSuggestions(data, max)
			if (!suggestions.length) {
				this._hideAutocomplete()
				return
			}

			this.updateAutocompleteResults(suggestions)
		} catch {
			// Swallow errors - autocomplete is a progressive enhancement
		}
	}

	private _extractAutocompleteSuggestions(data: unknown, max: number): string[] {
		if (!data || typeof data !== 'object') return []

		const anyData = data as {
			results?: ({ name?: string } | string)[]
			suggestions?: string[]
		}

		let rawSuggestions: string[] | undefined

		if (Array.isArray(anyData.results)) {
			rawSuggestions = anyData.results.map(item => {
				if (typeof item === 'string') return item
				if (item && typeof item === 'object' && typeof item.name === 'string') return item.name
				return ''
			})
		} else if (Array.isArray(anyData.suggestions)) {
			rawSuggestions = anyData.suggestions
		}

		if (!rawSuggestions) return []

		return rawSuggestions
			.map(suggestion => suggestion.trim())
			.filter(suggestion => suggestion.length > 0)
			.slice(0, max)
	}


	private _hideAutocomplete(): void {
		if (!this.dropdownEl || !this.inputEl) return

		this.dropdownEl.classList.remove('show')
		this.inputEl.setAttribute('aria-expanded', 'false')
		this.inputEl.removeAttribute('aria-activedescendant')
		// Remove aria-controls when dropdown is hidden to avoid referencing hidden content
		this.inputEl.removeAttribute('aria-controls')
		this.selectedIndex = -1
		this.isKeyboardNavigation = false
	}

	private _navigateAutocomplete(direction: number | 'start' | 'end'): void {
		if (this.autocompleteResults.length === 0) return

		// Mark that keyboard navigation is being used
		this.isKeyboardNavigation = true

		if (direction === 'start') {
			this.selectedIndex = 0
		} else if (direction === 'end') {
			this.selectedIndex = this.autocompleteResults.length - 1
		} else {
			this.selectedIndex += direction
			if (this.selectedIndex < 0) {
				this.selectedIndex = this.autocompleteResults.length - 1
			} else if (this.selectedIndex >= this.autocompleteResults.length) {
				this.selectedIndex = 0
			}
		}

		this._updateAutocompleteSelection()
		this._scrollToSelectedItem()
	}

	private _selectAutocompleteItem(index: number): void {
		if (index >= 0 && index < this.autocompleteResults.length) {
			this.inputEl.value = this.autocompleteResults[index]
			this.inputEl.classList.add('filled')
			this._updateButtons()
			this._hideAutocomplete()
			// Focus the input to maintain focus after selection
			this.inputEl.focus()
			this._handleSearch()
		}
	}

	private _updateAutocompleteSelection(): void {
		if (!this.dropdownEl || !this.inputEl) return

		const items = this.dropdownEl.querySelectorAll<HTMLAnchorElement>('.autocomplete-item')

		// Clear previous selection
		items.forEach(item => {
			item.classList.remove('selected', 'keyboard-focused')
		})

		// Set new selection
		if (this.selectedIndex >= 0 && this.selectedIndex < items.length) {
			const selectedItem = items[this.selectedIndex]
			selectedItem.classList.add('selected')

			if (this.isKeyboardNavigation) {
				selectedItem.classList.add('keyboard-focused')
			}

			this.inputEl.setAttribute('aria-activedescendant', selectedItem.id)
		} else {
			this.inputEl.removeAttribute('aria-activedescendant')
		}
	}

	private _scrollToSelectedItem(): void {
		if (!this.dropdownEl || this.selectedIndex < 0) return

		const items = this.dropdownEl.querySelectorAll<HTMLAnchorElement>('.autocomplete-item')
		if (this.selectedIndex >= items.length) return

		const selectedItem = items[this.selectedIndex]
		const dropdownRect = this.dropdownEl.getBoundingClientRect()
		const itemRect = selectedItem.getBoundingClientRect()

		const isItemVisible = (
			itemRect.top >= dropdownRect.top &&
			itemRect.bottom <= dropdownRect.bottom
		)

		if (!isItemVisible) {
			selectedItem.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'nearest'
			})
		}
	}

	public updateAutocompleteResults(results: string[]): void {
		// A search has been committed since these were requested, so showing them would reopen the
		// dropdown over the results. Typing again lifts the suppression.
		if (this.isAutocompleteSuppressed) return

		this.autocompleteResults = results
		this.selectedIndex = -1

		if (!this.dropdownEl || !this.inputEl || results.length === 0) {
			this._hideAutocomplete()
			return
		}

		const baseId = this.dropdownEl.id || 'autocomplete'

		this.dropdownEl.innerHTML = ''

		results.forEach((result, index) => {
			const item = document.createElement('a')
			item.href = '#'
			item.className = 'autocomplete-item'
			item.setAttribute('role', 'option')
			item.setAttribute('tabindex', '-1')
			item.id = `${baseId}-option-${index}`
			item.textContent = result
			if (this.dropdownEl) {
				this.dropdownEl.appendChild(item)
			}
		})

		// Add event handlers to each item (keeping individual handlers for now to maintain compatibility)
		this.dropdownEl.querySelectorAll<HTMLAnchorElement>('.autocomplete-item').forEach((item, index) => {
			// Click handler
			item.addEventListener('click', e => {
				e.preventDefault()
				this.isKeyboardNavigation = false
				this._selectAutocompleteItem(index)
			})

			// Mouse enter handler to reset keyboard navigation flag
			item.addEventListener('mouseenter', () => {
				this.isKeyboardNavigation = false
				// Clear keyboard focused class from all items
				const items = this.dropdownEl?.querySelectorAll<HTMLAnchorElement>('.autocomplete-item')
				items?.forEach(el => el.classList.remove('keyboard-focused'))
			})

			// Keyboard handler for individual items
			item.addEventListener('keydown', e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault()
					this._selectAutocompleteItem(index)
				} else if (e.key === 'ArrowDown') {
					e.preventDefault()
					this._navigateAutocomplete(1)
				} else if (e.key === 'ArrowUp') {
					e.preventDefault()
					this._navigateAutocomplete(-1)
				} else if (e.key === 'Tab') {
					e.preventDefault()
					if (e.shiftKey) {
						this._navigateAutocomplete(-1)
					} else {
						this._navigateAutocomplete(1)
					}
				} else if (e.key === 'Escape') {
					e.preventDefault()
					this._hideAutocomplete()
					this.inputEl.focus()
				}
			})
		})

		// Show dropdown and set ARIA expanded state
		this._positionDropdown()
		this.dropdownEl.classList.add('show')
		this.inputEl.setAttribute('aria-expanded', 'true')
		// Set aria-controls when dropdown is visible so it references valid content
		this.inputEl.setAttribute('aria-controls', this.dropdownEl.id)
	}

	private _handleWindowResize(): void {
		this._positionDropdown()
	}

	attributeChangedCallback(name: string, _oldValue: string, newValue: string) {
		if (name === 'data-theme') {
			if (this.dropdownEl) {
				if (newValue === null || newValue === undefined || newValue === '') {
					this.dropdownEl.removeAttribute('data-theme')
				} else {
					this.dropdownEl.setAttribute('data-theme', newValue)
				}
			}
		}
	}
}

if (!customElements.get('gmx-search')) {
	customElements.define('gmx-search', GMXSearch)
}